import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PageHeader } from "@excoleadershipui/pionweblib";
import { Link, useLocation } from "react-router-dom";
// material ui
import { Stack, Box, Grid, Paper, Fab, TextField, Avatar } from "@mui/material";
import { makeStyles } from "@mui/styles";

// icons
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
// components
import { MuiDataGrid, GridCellExpand, MuiTooltip } from "../../../../components";
import ConfirmDialog from "../../../../components/common/ConfirmDialog";
import Layout from "../../../../layout/Layout";
import AddL1Menu from "./AddL1Menu";
import EditL1Menu from "./EditL1Menu";
import AddL2Menu from './AddL2Menu';
import EditL2Menu from "./EditL2Menu";
import AddL3Menu from './AddL3Menu'
import EditL3Menu from "./EditL3Menu";
import { STATIC_IMAGE } from "../../../../utils/config";
// redux 
import { getL1MenuChr, rowSelect as l1RowSelect, deleteL1MenuChr, resetMessage as l1ResetMessage, resetErrMessage as l1ResetErrMessage } from "../../../../features/iam_chr/application_management/menu_management/l1MenuSlice"
import { getL2MenuChr, getL2MenuChrByL1Menu, rowSelect as l2RowSelect, deleteL2MenuChr, resetMessage as l2ResetMessage, resetErrMessage as l2ResetErrMessage } from '../../../../features/iam_chr/application_management/menu_management/l2MenuSlice'
import { getL3MenuChr, getL3MenuChrByL2Menu, rowSelect as l3RowSelect, deleteL3MenuChr, resetMessage as l3ResetMessage, resetErrMessage as l3ResetErrMessage } from '../../../../features/iam_chr/application_management/menu_management/l3MenuSlice'

// ------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "0.5rem",
    borderRadius: "1rem !important",
  },
}));

// ----------------------------------------------------------------------------------

const MenuManagement = () => {
  const [showL1Add, setShowL1Add] = useState(false);
  const [showL1Edit, setShowL1Edit] = useState(false);
  const [l1ConfirmDialog, setL1ConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [showL2Add, setShowL2Add] = useState(false);
  const [showL2Edit, setShowL2Edit] = useState(false);
  const [l2ConfirmDialog, setL2ConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [showL3Add, setShowL3Add] = useState(false);
  const [showL3Edit, setShowL3Edit] = useState(false);
  const [l3ConfirmDialog, setL3ConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  const [search, setSearch] = useState("");
  const [searchOne, setSearchOne] = useState("");
  const [searchTwo, setSearchTwo] = useState("");

  const classes = useStyles();
  const location = useLocation()
  const dispatch = useDispatch();

  const { l1MenuData, l1SelectedData, } = useSelector((state) => ({ ...state.l1MenusChr }))
  const { l2MenuData, l2SelectedData } = useSelector((state) => ({ ...state.l2MenusChr }))
  const { l3MenuData, l3SelectedData } = useSelector((state) => ({ ...state.l3MenusChr }))

  useEffect(() => {
    dispatch(getL1MenuChr());
    dispatch(getL2MenuChr());
    dispatch(getL3MenuChr());
  }, [])

  function renderCellExpand(params) {
    return (
      <GridCellExpand
        value={params.value || ""}
        width={params.colDef.computedWidth}
      />
    );
  }

  const l1Data = {
    rows: l1MenuData?.length > 0 ? l1MenuData : [] ,
    columns: [
      { field: "l1MenuName", headerName: "Menu Name", width: 200, renderCell: renderCellExpand },

      { field: "l1MenuGroupId", headerName: "Group Id", width: 100 },
      { field: "l1PageLink", headerName: "PageLink", width: 300, renderCell: renderCellExpand },
      {
        field: "l1MenuIcon",
        headerName: "Icon",
        width: 50,
        renderCell: (params) => {
          return (
            <Avatar
              alt="Icon"
              src={`${STATIC_IMAGE}/${params?.row?.l1MenuIcon}`}
              sx={{ width: 30, height: 30 }}
            />
          );
        },
      },
    ],
  };

  const handleSetL1ConfirmDialog = (obj) => {
    setL1ConfirmDialog({
      ...l1ConfirmDialog,
      isOpen: obj.isOpen,
    });
  };

  const handleDeleteL1MenuChr = () => {
    let data = {
      id: l1SelectedData?.id,
    };
    dispatch(deleteL1MenuChr(data)).then((res) => {
      if (res?.payload?.data?.ok) {
        setTimeout(() => {
          dispatch(l1ResetMessage());
          setL1ConfirmDialog({
            ...l1ConfirmDialog,
            isOpen: false,
          });
          dispatch(getL1MenuChr());
        }, 2000);
      }
      if (res?.payload?.error) {
        setTimeout(() => {
          dispatch(l1ResetErrMessage());
        }, 2000);
      }
    });
  };

  const l2Data = {
    rows: l1SelectedData?.id ? l2MenuData.filter((data)=> data?.level1MenuChr?.id === l1SelectedData?.id ) : [],
    columns: [
      { field: "l2MenuName", headerName: "Menu Name", width: 200, renderCell: renderCellExpand },

      { field: "l2MenuGroupId", headerName: "Group Id", width: 100 },
      { field: "l2PageLink", headerName: "PageLink", width: 300, renderCell: renderCellExpand },
      {
        field: "l2MenuIcon",
        headerName: "Icon",
        width: 50,
        renderCell: (params) => {
          return (
            <Avatar
              alt="Icon"
              src={`${STATIC_IMAGE}/${params?.row?.l2MenuIcon}`}
              sx={{ width: 30, height: 30 }}
            />
          );
        },
      },
    ],
  };

  const handleSetL2ConfirmDialog = (obj) => {
    setL2ConfirmDialog({
      ...l2ConfirmDialog,
      isOpen: obj.isOpen,
    });
  };

  const handleDeleteL2MenuChr = () => {
    let data = {
      id: l2SelectedData?.id,
    };
    dispatch(deleteL2MenuChr(data)).then((res) => {
      if (res?.payload?.data?.ok) {
        setTimeout(() => {
          dispatch(l2ResetMessage());
          setL2ConfirmDialog({
            ...l2ConfirmDialog,
            isOpen: false,
          });
          dispatch(getL2MenuChr());
        }, 2000);
      }
      if (res?.payload?.error) {
        setTimeout(() => {
          dispatch(l2ResetErrMessage());
        }, 2000);
      }
    });
  };




  const l3Data = {
    rows: l2SelectedData?.id ? l3MenuData.filter((data)=> data?.level2MenuChr?.id === l2SelectedData?.id) : [],
    columns: [
      { field: "l3MenuName", headerName: "Menu Name", width: 200, renderCell: renderCellExpand },

      { field: "l3MenuGroupId", headerName: "Group Id", width: 100 },
      { field: "l3PageLink", headerName: "PageLink", width: 300, renderCell: renderCellExpand },
      {
        field: "l3MenuIcon",
        headerName: "Icon",
        width: 50,
        renderCell: (params) => {
          return (
            <Avatar
              alt="Icon"
              src={`${STATIC_IMAGE}/${params?.row?.l3MenuIcon}`}
              sx={{ width: 30, height: 30 }}
            />
          );
        },
      },
    ],
  };

  const handleSetL3ConfirmDialog = (obj) => {
    setL3ConfirmDialog({
      ...l3ConfirmDialog,
      isOpen: obj.isOpen,
    });
  };

  const handleDeleteL3MenuChr = () => {
    let data = {
      id: l3SelectedData?.id,
    };
    dispatch(deleteL3MenuChr(data)).then((res) => {
      if (res?.payload?.data?.ok) {
        setTimeout(() => {
          dispatch(l3ResetMessage());
          setL3ConfirmDialog({
            ...l3ConfirmDialog,
            isOpen: false,
          });
          dispatch(getL3MenuChr());
        }, 2000);
      }
      if (res?.payload?.error) {
        setTimeout(() => {
          dispatch(l3ResetErrMessage());
        }, 2000);
      }
    });
  };

  return (
    <>
      {/* <Link to="/exco/iam/app-services" state={{ l3Menu: location?.state?.l3Menu }}>
        <CancelIcon color="red" sx={{ position: "absolute", right: 15, zIndex: 1 }} />
      </Link> */}
      <Grid mt={0.5} container spacing={2}>
        <Grid item xs={12} sm={12} md={4}>
          <Paper elevation={4} className={classes.paper}>
            <PageHeader variant="pageSecHeader">L1 Menus</PageHeader>
            {/* <Stack
              flexDirection="row"
              justifyContent="space-between"
              textAlign="center"
              position="relative"
              mb={1}
            >
             
              {
              
              // location?.state?.permissions?.find((e) => e.applicationService?.name?.split(".")[e?.applicationService?.name?.split(".")?.length - 1] === "l1MenuCreate") &&
                <MuiTooltip title="Add" placement="bottom">
                  <Fab
                    aria-label="add"
                    onClick={() => setShowL1Add(true)}
                    color="primary"
                    size="small"
                  >
                    <AddIcon />
                  </Fab>
                </MuiTooltip>
              }
            </Stack> */}
            <MuiDataGrid
              setShowAdd={() => setShowL1Add(true)}
              // setShowEdit={location?.state?.permissions?.find((e) => e.applicationService?.name?.split(".")[e?.applicationService?.name?.split(".")?.length - 1] === "l1MenuUpdate") ? () => setShowL1Edit(true) : null}
              // setShowDelete={location?.state?.permissions?.find((e) => e.applicationService?.name?.split(".")[e?.applicationService?.name?.split(".")?.length - 1] === "l1MenuDelete") ? () =>
              //   setL1ConfirmDialog({
              //     isOpen: true,
              //     title: "Are you sure to delete this record?",
              //     subTitle: "You can't undo this operation",
              //     onConfirm: () => handleDeleteL1MenuChr(),
              //   }) : null
              // }
              setShowEdit={ () => setShowL1Edit(true) }
              setShowDelete={ () =>
                setL1ConfirmDialog({
                  isOpen: true,
                  title: "Are you sure to delete this record?",
                  subTitle: "You can't undo this operation",
                  onConfirm: () => handleDeleteL1MenuChr(),
                })
              }
              toolbarFilter={true}
              data={l1Data}
              select={(data) => {
                dispatch(l1RowSelect(data))
                // dispatch(getL2MenuChrByL1Menu(data[0]?.id))
              }}
            />
            <AddL1Menu showAdd={showL1Add} setShowAdd={setShowL1Add} />
            <EditL1Menu showEdit={showL1Edit} setShowEdit={setShowL1Edit} />
            <ConfirmDialog
              confirmDialog={l1ConfirmDialog}
              setConfirmDialog={(obj) => handleSetL1ConfirmDialog(obj)}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Paper elevation={4} className={classes.paper}>
            <PageHeader variant="pageSecHeader">L2 Menus</PageHeader>
            {/* <Stack
              flexDirection="row"
              justifyContent="space-between"
              textAlign="center"
              position="relative"
              mb={1}
            >
            
              
                <MuiTooltip title="Add" placement="bottom">
                  <Fab
                    aria-label="add"
                    onClick={() => setShowL2Add(true)}
                    color="primary"
                    size="small"
                  >
                    <AddIcon />
                  </Fab>
                </MuiTooltip>
              
            </Stack> */}
            <MuiDataGrid
              // setShowEdit={location?.state?.permissions?.find((e) => e.applicationService?.name?.split(".")[e?.applicationService?.name?.split(".")?.length - 1] === "l2MenuUpdate") ? () => setShowL2Edit(true) : null}
              // setShowDelete={location?.state?.permissions?.find((e) => e.applicationService?.name?.split(".")[e?.applicationService?.name?.split(".")?.length - 1] === "l2MenuDelete") ? () =>
              //   setL2ConfirmDialog({
              //     isOpen: true,
              //     title: "Are you sure to delete this record?",
              //     subTitle: "You can't undo this operation",
              //     onConfirm: () => handleDeleteL2MenuChr(),
              //   }) : null
              // }
              setShowAdd={() => setShowL2Add(true)}
              setShowEdit={ () => setShowL2Edit(true) }
              setShowDelete={ () =>
                setL2ConfirmDialog({
                  isOpen: true,
                  title: "Are you sure to delete this record?",
                  subTitle: "You can't undo this operation",
                  onConfirm: () => handleDeleteL2MenuChr(),
                }) 
              }
              toolbarFilter={true}
              data={l2Data}
              select={(data) => {
                dispatch(l2RowSelect(data))
                // dispatch(getL3MenuChrByL2Menu(data[0]?.id))
              }}
            />
            <AddL2Menu showAdd={showL2Add} setShowAdd={setShowL2Add} />
            <EditL2Menu showEdit={showL2Edit} setShowEdit={setShowL2Edit} />
            <ConfirmDialog
              confirmDialog={l2ConfirmDialog}
              setConfirmDialog={(obj) => handleSetL2ConfirmDialog(obj)} />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Paper elevation={4} className={classes.paper}>
            <PageHeader variant="pageSecHeader">L3 Menus</PageHeader>
            {/* <Stack
              flexDirection="row"
              justifyContent="space-between"
              textAlign="center"
              position="relative"
              mb={1}
            >
             
             
                <MuiTooltip title="Add" placement="bottom">
                  <Fab
                    aria-label="add"
                    onClick={() => setShowL3Add(true)}
                    color="primary"
                    size="small"
                  >
                    <AddIcon />
                  </Fab>
                </MuiTooltip>
              
            </Stack> */}
            <MuiDataGrid
               setShowAdd={() => setShowL3Add(true)}
              // setShowEdit={location?.state?.permissions?.find((e) => e.applicationService?.name?.split(".")[e?.applicationService?.name?.split(".")?.length - 1] === "l3MenuUpdate") ? () => setShowL3Edit(true) : null}
              // setShowDelete={location?.state?.permissions?.find((e) => e.applicationService?.name?.split(".")[e?.applicationService?.name?.split(".")?.length - 1] === "l3MenuDelete") ? () =>
              //   setL3ConfirmDialog({
              //     isOpen: true,
              //     title: "Are you sure to delete this record?",
              //     subTitle: "You can't undo this operation",
              //     onConfirm: () => handleDeleteL3MenuChr(),
              //   }) : null
              // }
               setShowEdit={() =>  setShowL3Edit(true)}
               setShowDelete={ () =>
                 setL3ConfirmDialog({
                   isOpen: true,
                   title: "Are you sure to delete this record?",
                   subTitle: "You can't undo this operation",
                   onConfirm: () => handleDeleteL3MenuChr(),
                 }) 
               }
              toolbarFilter={true}
              data={l3Data}
              select={(data) => {
                dispatch(l3RowSelect(data))
              }}
            />
            <AddL3Menu showAdd={showL3Add} setShowAdd={setShowL3Add} />
            <EditL3Menu showEdit={showL3Edit} setShowEdit={setShowL3Edit} />
            <ConfirmDialog
              confirmDialog={l3ConfirmDialog}
              setConfirmDialog={(obj) => handleSetL3ConfirmDialog(obj)} />
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default MenuManagement;
